import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { works } from "@/mocks/works";
import { posts } from "@/mocks/posts";

interface CommentItem {
  id: number;
  name: string;
  time: string;
  content: string;
  color: "primary" | "accent" | "secondary";
}

const initialComments: CommentItem[] = [
  {
    id: 1,
    name: "漫圈老油条",
    time: "2小时前",
    content: "前排打卡！这部我也在追，终于找到组织了，漫圈里聊得超开心。",
    color: "primary",
  },
  {
    id: 2,
    name: "考据党阿黄",
    time: "1小时前",
    content: "楼主分析得很到位，特别是那个细节回收，我也注意到了，补充一点我的看法……",
    color: "accent",
  },
  {
    id: 3,
    name: "追漫不熬夜",
    time: "30分钟前",
    content: "刚入坑，请问这部从哪一话开始看比较好？看到讨论区氛围这么好就放心了。",
    color: "secondary",
  },
];

const nameColor = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function PostDetail() {
  const { id } = useParams();
  const work = useMemo(() => works.find((w) => String(w.id) === id) ?? works[0], [id]);
  const post = useMemo(
    () => posts.find((p) => p.work === work.title) ?? posts[0],
    [work],
  );

  useSeo(
    `${post.title} - ${work.title}讨论区 - 178漫画网同好交流`,
    `${post.title}。在178漫画网找到追${work.title}的同好，一起交流剧情、分享心得，参与漫圈讨论。`,
  );

  const [comments, setComments] = useState<CommentItem[]>(initialComments);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("请先填写昵称");
      return;
    }
    if (!content.trim()) {
      setError("请输入留言内容");
      return;
    }
    const colorList: CommentItem["color"][] = ["primary", "accent", "secondary"];
    const newComment: CommentItem = {
      id: Date.now(),
      name: name.trim(),
      time: "刚刚",
      content: content.trim(),
      color: colorList[comments.length % 3],
    };
    setComments((prev) => [newComment, ...prev]);
    setName("");
    setContent("");
    setError("");
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-20">
        <section className="bg-background-100 border-b border-background-200">
          <div className="mx-auto max-w-5xl px-4 md:px-6 py-10 md:py-14">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/works" className="hover:text-primary-600">漫画作品</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/post/${work.id}`} className="text-foreground-700">{work.title}讨论区</Link>
            </nav>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl leading-snug text-foreground-950">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                  {post.author.slice(0, 1)}
                </span>
                {post.author}
              </span>
              <span>{post.time}</span>
              <span className="inline-flex items-center gap-1"><i className="ri-eye-line" />{post.views} 浏览</span>
              <span className="inline-flex items-center gap-1"><i className="ri-message-3-line" />{post.replies} 回复</span>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-14">
          <div className="mx-auto max-w-5xl px-4 md:px-6 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            {/* 正文 */}
            <article className="lg:col-span-2">
              <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
                <p className="text-base md:text-lg leading-8 text-foreground-800">{post.excerpt}</p>

                <div className="mt-6 space-y-5 text-sm md:text-base leading-7 text-foreground-700">
                  <p>
                    最近《{work.title}》的更新在漫圈里掀起了一波热烈讨论，同好们纷纷来到
                    <strong className="text-primary-700"> 178漫画网讨论区 </strong>
                    分享自己的解读。楼主这篇帖子整理了目前最受认可的几种观点，供大家参考讨论。
                  </p>
                  <p>
                    首先，很多同好都注意到剧情里埋下的伏笔。作者在细节处理上一向很用心，回头重看前面的章节，会发现不少呼应之处。这也是为什么大家愿意在漫圈里反复复盘、互相印证。
                  </p>
                  <p>
                    其次，关于人物的动机，不同漫友有不同的看法。有人觉得是性格使然，也有人认为是环境所迫。欢迎在下方留言，说出你的观点，我们一起去漫圈里继续聊。
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
                  <span className="text-xs text-foreground-500">相关标签：</span>
                  {work.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-600">#{t}</span>
                  ))}
                </div>
              </div>

              {/* 留言 */}
              <div className="mt-8">
                <h2 className="font-heading text-xl text-foreground-950">
                  讨论区留言 <span className="text-sm font-sans font-normal text-foreground-500">（{comments.length}）</span>
                </h2>

                <form onSubmit={submit} className="mt-4 rounded-2xl border border-background-200 bg-background-100 p-4 md:p-5">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="你的昵称"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    maxLength={500}
                    placeholder="聊聊你对剧情的看法，分享你的心得……"
                    className="mt-3 h-24 w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{content.length}/500</span>
                    <span className="text-xs text-foreground-400 text-right">{error && <span className="text-primary-600">{error}</span>}</span>
                    <button
                      type="submit"
                      className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
                    >
                      发表留言
                    </button>
                  </div>
                </form>

                <div className="mt-5 flex flex-col gap-3">
                  {comments.map((c) => (
                    <div key={c.id} className="rounded-2xl border border-background-200 bg-background-100 p-4">
                      <div className="flex items-center gap-2">
                        <span className={`grid h-8 w-8 place-items-center rounded-full text-sm font-bold ${nameColor[c.color]}`}>
                          {c.name.slice(0, 1)}
                        </span>
                        <span className="text-sm font-medium text-foreground-900">{c.name}</span>
                        <span className="ml-auto text-xs text-foreground-400">{c.time}</span>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-foreground-700">{c.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            {/* 侧栏 */}
            <aside className="flex flex-col gap-5">
              <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
                  <img src={work.cover} alt={`${work.title}漫画封面`} className="h-full w-full object-cover object-top" />
                  <span className="absolute left-2 top-2 rounded-full bg-primary-500 text-background-50 px-2.5 py-1 text-xs font-medium">{work.status}</span>
                </div>
                <h2 className="mt-4 font-heading text-lg text-foreground-950">{work.title}</h2>
                <p className="mt-1 text-xs text-foreground-500">{work.author} · {work.category} · {work.episodes}话</p>
                <p className="mt-3 text-sm leading-6 text-foreground-600 line-clamp-4">{work.synopsis}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {work.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-200/70 px-2.5 py-0.5 text-xs text-foreground-600">#{t}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-600">
                  <span className="inline-flex items-center gap-1"><i className="ri-user-heart-line text-primary-500" />{work.followers} 同好</span>
                  <span className="font-bold text-secondary-600">★ {work.rating}</span>
                </div>
                <Link
                  to="/circles"
                  className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
                >
                  进圈和同好一起聊 <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-base text-foreground-950">相关推荐</h3>
                <div className="mt-4 flex flex-col gap-3">
                  {posts.filter((p) => p.id !== post.id).slice(0, 4).map((p) => (
                    <Link key={p.id} to={`/post/${works.find((w) => w.title === p.work)?.id ?? 1}`} className="group flex items-start gap-2">
                      <i className="ri-arrow-right-s-line mt-0.5 text-primary-500" />
                      <span className="text-sm leading-5 text-foreground-700 group-hover:text-primary-600 line-clamp-2">{p.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}